import { DOCUMENT, isPlatformBrowser, isPlatformServer, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, NgZone, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { lastValueFrom } from 'rxjs';
import { ScreenSizes } from './classes/screen-sizes.class';
import { ContextMenuGlobalComponent } from './components/context-menu-global/context-menu-global.component';
import { CookieBannerComponent } from './components/cookie-banner/cookie-banner.component';
import { DialogsComponent } from './components/dialogs/dialogs.component';
import { MobileMenusComponent } from './components/mobile-menus/mobile-menus.component';
import { ToastsComponent } from './components/toasts/toasts.component';
import { TooltipGlobalComponent } from './components/tooltip-global/tooltip-global.component';
import { assetLoadSettings, thirdPartyScriptDelay } from './consts';
import { getFullPath } from './lib/get-full-path';
import { loadScript } from './lib/load-script';
import { parseJwt } from './lib/parse-jwt';
import { sleep } from './lib/sleep';
import { AppConfigService } from './providers/config.service';
import { BrowserService } from './services/browser.service';
import { ContextMenuService } from './services/context-menu.service';
import { DialogService } from './services/dialog.service';
import { FeatureFlagsService } from './services/feature-flags.service';
import { StateService } from './services/state.service';
import { RouteLoaderComponent } from './shared/components/route-loader/route-loader.component';
import { ResizeDirective } from './shared/directives/resize.directive';
import { SwipeDirection, SwipeDirective } from './shared/directives/swipe.directive';

import * as Sentry from '@sentry/angular';
import { DebugDialogComponent } from './dialogs/debug-dialog/debug-dialog.component';
import { randomString } from './lib/random-string';
import { StorageService } from './services/storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ResizeDirective,
    SwipeDirective,
    RouterOutlet,
    DialogsComponent,
    MobileMenusComponent,
    ToastsComponent,
    ContextMenuGlobalComponent,
    TooltipGlobalComponent,
    RouteLoaderComponent,
    CookieBannerComponent,
  ],
})
export class AppComponent {
  public routeLoading = true;
  public showCookieBanner: boolean;
  showGlobalGoogleLogin = false;
  oneTapTopSpacing: number = 10;
  loadedGoogleOneTap = false;
  extensionMode: boolean;
  lastVersionCheck = Date.now();

  onSwipeDown(direction: SwipeDirection) {
    if (direction === 'down') {
      this.dialog.close();
      this.contextMenuService.closeContext();
    }
  }

  constructor(
    public state: StateService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private dialog: DialogService,
    private contextMenuService: ContextMenuService,
    private route: ActivatedRoute,
    private browserService: BrowserService,
    private http: HttpClient,
    private config: AppConfigService,
    public featureFlags: FeatureFlagsService,
    private zone: NgZone,
    private cookieService: CookieService, // _trace: Sentry.TraceService // for sentry tracing
    private storage: StorageService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      if (location.search.includes('debugMode')) {
        this.state.recordDebug = true;
        this.dialog.open(DebugDialogComponent);
      }
      if (this.config.env !== 'production') {
        // temporary for playing around with asset loading
        const backupAssetLoadSettings = { ...assetLoadSettings };
        window['setAssetLoadSettings'] = (newSettings = {}) => {
          Object.assign(assetLoadSettings, newSettings);
          console.log('setAssetLoadSettings(' + JSON.stringify(assetLoadSettings, null, 2) + ')');
          this.storage.set('assetLoadSettings', assetLoadSettings);
        };
        window['resetAssetLoadSettings'] = () => {
          window['setAssetLoadSettings'](backupAssetLoadSettings);
        };

        if (this.storage.has('assetLoadSettings')) {
          window['setAssetLoadSettings'](this.storage.get('assetLoadSettings'));
        }
      }

      document.body.addEventListener(
        'error',
        event => {
          if (!event.target) return;
          if (event.target['tagName'] === 'IMG') {
            const src = event.target['src'];
            if (!src.includes('picflow.io')) return;

            // Attempt to fetch the image separately to get the status code
            const fetchSrc = src + (src.includes('?') ? '&' : '?') + 'statusCheck=true';
            fetch(fetchSrc, { method: 'HEAD' })
              .then(response => {
                if (!response.ok) {
                  Sentry.captureMessage(`Failed to load image: ${src} - Status: ${response.status}`, 'warning');
                }
              })
              .catch(error => {
                Sentry.captureMessage(`Failed to fetch image: ${src} - Error: ${error}`, 'warning');
              });
          }
        },
        true // useCapture - necessary for resource loading errors
      );
    }

    Sentry.configureScope(scope => {
      scope.addEventProcessor(event => {
        event.environment = this.config.env;
        return event;
      });
    });
    Sentry.setTag('is_ssr', isPlatformServer(this.platformId));

    this.extensionMode = this.browserService.isExtension();
    if (this.config.env !== 'local' && !this.extensionMode) this.loadConsoleMessage();

    if (isPlatformServer(this.platformId)) this.routeLoading = false;

    let previousUrl = '';
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // No route animation when switching to the asset view
        const galleryToAsset =
          this.browserService.urlIsGalleryUrl(previousUrl) && this.browserService.urlIsAssetUrl(event.url);
        const assetToGallery =
          this.browserService.urlIsAssetUrl(previousUrl) && this.browserService.urlIsGalleryUrl(event.url);

        if (!galleryToAsset && !assetToGallery) this.routeLoading = true;
      }
      if (event instanceof NavigationEnd) {
        this.checkVersion();
        this.routeLoading = false;
        if (!previousUrl) {
          this.handleCookieBanner();
        } else if (!this.browserService.getCookies()['accepted-cookies']) {
          this.hideCookieBanner();
        }

        this.state.pathHistory.push('/' + getFullPath(this.route.snapshot).join('/'));
        this.showGlobalGoogleLogin = this.canShowGoogleLogin();
        if (!isPlatformServer(this.platformId) && ScreenSizes.screenSize() === 'mobile') {
          // Not removing the class is on purpose.
          if (this.showGlobalGoogleLogin) this.document.body.classList.add('show-mobile-google-login');
        } else {
          this.oneTapTopSpacing = this.getOneTapTopSpacing();
        }
        if (this.showGlobalGoogleLogin) this.loadGoogleOneTap();

        previousUrl = event.urlAfterRedirects;
      }
      if (event instanceof NavigationCancel) this.routeLoading = false;
      if (event instanceof NavigationError) this.routeLoading = false;
    });
  }

  private loadGoogleOneTap() {
    if (this.loadedGoogleOneTap) return;
    this.zone.runOutsideAngular(async () => {
      await sleep(thirdPartyScriptDelay); // Waiting a bit before loading the google one tap client
      this.loadedGoogleOneTap = true;
      loadScript('https://accounts.google.com/gsi/client', { ignoreErrors: true }); // for one tap
      window['googleOneTapPostLogin'] = (e: any) => {
        if (!e?.credential) return;
        const content = parseJwt(e.credential);
        if (!content) return;
        this.router.navigate(['/auth', 'login', 'google'], { queryParams: { email: content.email } });
      };
    });
  }

  private getOneTapTopSpacing() {
    if (isPlatformServer(this.platformId)) return 5;
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'auth') return 5;
    return 86;
  }

  private canShowGoogleLogin(): boolean {
    if (isPlatformServer(this.platformId)) return false;
    if (this.extensionMode) return false;
    if (this.browserService.getCookies()?.['picflow-loggedin'] === 'true') return false;
    const path = getFullPath(this.route.snapshot);
    if (this.featureFlags.get('MAINTENANCE_MODE')) return;
    if (this.route.snapshot.queryParams.noOneTap) return false;
    if (this.config.isTenantDomain) return false;
    if (path[0] === 'gallery') return false;
    if (path[0] === 'pricing') return false;
    if (path[0] === 'affiliates') return false;
    if (path[0] === 'learn') return false;
    if (path[0] === 'blog') return false;
    if (path[0] === 'press') return false;
    if (path[0] === 'maintenance') return false;
    if (path[0] === 'auth' && this.route.snapshot.queryParams.email) return false;
    if (ScreenSizes.screenSize() === 'mobile') return path[0] === 'auth';
    return true;
  }

  public hideCookieBanner() {
    this.showCookieBanner = false;
    this.cookieService.set('accepted-cookies', 'true', 9627576230894, '/');
  }

  private handleCookieBanner() {
    if (this.extensionMode) return;
    if (this.route.snapshot.queryParams.ref) return;
    if (this.browserService.getCookies()['accepted-cookies']) return;
    if (this.route.snapshot.queryParams._storyblok) return;
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'a') return;
    if (path[0] === 'lp') return; // https://app.asana.com/0/0/1204548507001151/f
    if (path.includes('gallery')) return;
    if (this.config.isTenantDomain) return;
    this.showCookieBanner = true;
  }

  private loadConsoleMessage() {
    if (isPlatformServer(this.platformId)) return;
    this.zone.runOutsideAngular(async () => {
      await sleep(1000 * 2);
      try {
        const message = await lastValueFrom(this.http.get('/assets/console-comment.txt', { responseType: 'text' }));
        console.log(message);
      } catch {}
    });
  }

  private checkVersion = async () => {
    if (Date.now() - this.lastVersionCheck < 1000 * 60 * 5) return;
    if (isPlatformServer(this.platformId)) return;
    if (this.extensionMode) return;
    this.lastVersionCheck = Date.now();
    if (this.config.env === 'local') return;

    const resp = await lastValueFrom(
      this.http.get<{ version: string }>(`/internal-api/version?cacheBust=${randomString(6)}`)
    );
    if (resp.version && resp.version !== window['picflowVersion']) {
      location.reload();
    }
  };
}
